import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { useCalculateSplit, useLedgerTransactions, useLedgerStats, useMyLedgerStats, useManualContribution, useLedgerFundraisers, useLedgerContributions } from '../hooks/useLedger';
import { useQueryClient } from '@tanstack/react-query';
import { apiWorker, PaginatedResponse, getStripeConnectLink, getStripeDashboardLink, exportNodeLedger } from '../api/worker';
import { PaginationControls } from '../components/ui/PaginationControls';
import { useDirectory } from '../hooks/useDirectory';
import { PaymentForm } from '../components/ledger/PaymentForm';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { 
  Download, 
  Search, 
  Filter, 
  FileText, 
  ArrowUpRight, 
  ArrowDownLeft,
  DollarSign,
  TrendingUp,
  Clock,
  Plus
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { cn, safeFormat } from '../lib/utils';
import { SplitBreakdown } from '../components/ledger/SplitBreakdown';
import { Transaction, SplitResult, Contribution } from '../types';
import toast from 'react-hot-toast';

export const LedgerPage = () => {
  const { node, brand } = useTenant();
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const [includeSubnodes, setIncludeSubnodes] = React.useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [isConnectingStripe, setIsConnectingStripe] = React.useState(false);
  const [isLoadingDashboard, setIsLoadingDashboard] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  
  const [isManualContributionModalOpen, setIsManualContributionModalOpen] = React.useState(false);
  const [manualAmount, setManualAmount] = React.useState('');
  const [manualDescription, setManualDescription] = React.useState('');

  const terms = node?.config?.terminology;
  const isAdmin = can('view_ledger_admin');
  const canPay = can('view_personal_finances');
  
  const { data: transactionsData, isLoading } = useLedgerTransactions(node?.id || '', includeSubnodes, page);
  
  const transactions = transactionsData?.content || [];
  
  const { data: fundraisers = [] } = useLedgerFundraisers(node?.id || '');

  // Dependent filtering not supported by updated API; use empty set for compatibility
  const dependentUserIds: string[] = [];

  // Use unique identifiers for strict filtering
  const myTransactions = transactions.filter(t => 
    (t.userId && user?.id && t.userId === user.id) || 
    (t.userName && user?.name && t.userName === user.name) ||
    (t.userId && dependentUserIds.includes(t.userId))
  );
  
  const { data: statsData } = useLedgerStats(node?.id || '', includeSubnodes, isAdmin);
  const { data: myStatsData } = useMyLedgerStats(node?.id || '', isAdmin);

  const stats = React.useMemo(() => {
    if (isAdmin) {
      return statsData || { total: 0, pending: 0, count: 0 };
    } else {
      return myStatsData || { total: 0, pending: 0, count: 0 };
    }
  }, [statsData, myStatsData, isAdmin]);

  // Dynamic fee requirement from node config
  const requiredDues = node?.config?.requiredDues || 0;
  const paidDues = myTransactions
    .filter(t => t.status === 'PAID')
    .reduce((acc, t) => acc + t.amount, 0);
  const duesPercentage = requiredDues > 0 ? Math.min(Math.round((paidDues / requiredDues) * 100), 100) : (paidDues > 0 ? 100 : 0);

  const exportCsv = async () => {
    if (isAdmin && node?.id) {
      try {
        setIsExporting(true);
        const blob = await exportNodeLedger(node.id);
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `ledger_${node.slug}_${safeFormat(new Date(), 'yyyy-MM-dd')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        toast.success("Ledger exported successfully");
      } catch (error) {
        toast.error("Failed to export ledger");
      } finally {
        setIsExporting(false);
      }
    } else {
      const dataToExport = myTransactions;
      const headers = ['Date', 'Member', 'Amount', 'Description', 'Status', 'Stripe ID'];
      const rows = dataToExport.map(t => [
        safeFormat(t.createdAt, 'yyyy-MM-dd HH:mm'),
        t.isAnonymous ? 'Anonymous Benefactor' : t.userName,
        t.amount.toFixed(2),
        t.description,
        t.status,
        t.stripePaymentIntentId || ''
      ]);
      
      const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `my_financials_${node?.slug}_${safeFormat(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const [splitAmount, setSplitAmount] = React.useState<number>(100);
  const [txSearch, setTxSearch] = React.useState('');
  const [txStatusFilter, setTxStatusFilter] = React.useState<'ALL' | 'PAID' | 'PENDING' | 'FAILED'>('ALL');
  const { data: splitResults = [], isLoading: isSplitsLoading } = useCalculateSplit(node?.id || '', splitAmount);

  const { data: contributions = [] } = useLedgerContributions(node?.id || '');

  const isStripeConnected = Boolean(
    (node as any)?.stripeConnectAccountId ||
    (node as any)?.chargesEnabled ||
    (node?.config as any)?.stripeAccountId
  );

  const handleConnectStripe = async () => {
    if (!node) return;
    try {
      setIsConnectingStripe(true);
      const data = await getStripeConnectLink(node.id);
      if (data.url) window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to get Stripe connect link');
    } finally {
      setIsConnectingStripe(false);
    }
  };

  const handleViewDashboard = async () => {
    if (!node) return;
    try {
      setIsLoadingDashboard(true);
      const data = await getStripeDashboardLink(node.id);
      if (data.url) window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to load Stripe dashboard');
    } finally {
      setIsLoadingDashboard(false);
    }
  };

  const manualContributionMutation = useManualContribution(node?.id || '');

  const handleManualContribution = (e: React.FormEvent) => {
    e.preventDefault();
    if (!node?.id) return;
    
    manualContributionMutation.mutate({
      amount: parseFloat(manualAmount),
      description: manualDescription
    }, {
      onSuccess: () => {
        toast.success('Manual payment recorded successfully');
        setIsManualContributionModalOpen(false);
        setManualAmount('');
        setManualDescription('');
      },
      onError: () => toast.error('Failed to record manual payment')
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">{isAdmin ? (terms?.ledger || 'Financials') : 'My Financials'}</h1>
          <p className="text-xs md:text-sm text-gray-500">
            {isAdmin ? 'Manage organization revenue and dues.' : 'Track your personal contributions and dues.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-1.5 rounded-none border-2 border-black dark:border-white dark:bg-slate-900">
              <input 
                type="checkbox" 
                checked={includeSubnodes} 
                onChange={(e) => setIncludeSubnodes(e.target.checked)}
                className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-600"
              />
              <span className="text-xs font-semibold text-gray-700">Include {terms?.childNode ? `${terms.childNode}s` : 'Sub-orgs'}</span>
            </label>
          )}

          <Button variant="secondary" onClick={exportCsv} isLoading={isExporting}>
            <Download size={18} />
            {isAdmin ? 'Export CSV' : 'Export History'}
          </Button>
          {isAdmin && (
             <Button variant="primary" onClick={() => setIsManualContributionModalOpen(true)}>
               <Plus size={18} />
               Manual Entry
             </Button>
          )}
        </div>
      </div>

      {/* Dues Progress Card - Always visible if user has a dues goal or transactions */}
      <Card className="bg-brand-primary p-6 text-white border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-white/10 rounded-none blur-2xl dark:bg-slate-900" />
        <div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-24 h-24 bg-brand-secondary/20 rounded-none blur-2xl" />
        
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2">
            <p className="text-brand-primary-light text-xs font-bold uppercase tracking-widest font-mono">
              {dependentUserIds.length > 0 ? 'Combined Dues Progress' : 'My Dues Progress'}
            </p>
            <h2 className="text-3xl font-black">${(paidDues || 0).toLocaleString()} <span className="text-white/40 text-lg font-medium self-end">/ ${(requiredDues || 0).toLocaleString()}</span></h2>
            <p className="text-sm text-blue-200">
              {dependentUserIds.length > 0 
                ? `You and your ${dependentUserIds.length} dependents have fulfilled ${duesPercentage}% of the contribution goal.`
                : `You have fulfilled ${duesPercentage}% of your annual contribution goal.`}
            </p>
          </div>

          <div className="w-full md:w-64 space-y-3">
            <div className="h-4 bg-navy-900/40 rounded-none overflow-hidden p-1 shadow-inner">
              <div 
                className="h-full bg-brand-primary-light rounded-none transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(255,255,255,0.4)]"
                style={{ width: `${duesPercentage}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] uppercase font-bold tracking-widest font-mono text-blue-300">
              <span>0%</span>
              <span>{duesPercentage}% Completed</span>
              <span>100%</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {duesPercentage === 100 ? (
              <div className="p-3 bg-white/10 rounded-none border-2 border-black dark:border-white border-white/20 backdrop-blur-sm dark:bg-slate-900">
                <Badge variant="green" className="bg-green-500/20 text-green-300 border-none">STATUS: GOOD STANDING</Badge>
              </div>
            ) : (
              <div className="p-3 bg-white/10 rounded-none border-2 border-black dark:border-white border-white/20 backdrop-blur-sm dark:bg-slate-900">
                 <Badge variant="amber" className="bg-amber-500/20 text-amber-300 border-none">PARTIAL PAYMENT</Badge>
              </div>
            )}
          </div>
        </div>
      </Card>

      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card variant="stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-50 text-green-700 rounded-none">
                <DollarSign size={20} />
              </div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">Total Revenue</p>
            </div>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">${(stats.total || 0).toLocaleString()}</p>
            <p className="text-xs text-green-700 mt-1">All time</p>
          </Card>

          <Card variant="stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-50 text-amber-700 rounded-none">
                <Clock size={20} />
              </div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">Pending</p>
            </div>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">${(stats.pending || 0).toLocaleString()}</p>
            <p className="text-xs text-amber-700 mt-1">Awaiting confirmation</p>
          </Card>

          <Card variant="stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-50 text-purple-700 rounded-none">
                <TrendingUp size={20} />
              </div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">Transactions</p>
            </div>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.count}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total volume</p>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Payment Form (for members) or Summary (for admins) */}
        <div className="lg:col-span-1 space-y-6">
          {fundraisers.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest font-mono">Active Fundraisers</h3>
              {fundraisers.map((f: any) => (
                <Card key={f.id} className="p-4 border-l-4 border-l-brand-primary">
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white">{f.title}</h4>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 mb-3">{f.description}</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-[10px] font-bold font-mono">
                      <span className="text-brand-primary">${(f.currentValue || 0).toLocaleString()}</span>
                      <span className="text-gray-400">Target: ${(f.targetValue || 0).toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-white rounded-none overflow-hidden dark:bg-slate-800">
                      <div 
                        className="h-full bg-brand-primary rounded-none transition-all duration-1000"
                        style={{ width: `${Math.min(Math.round((f.currentValue / f.targetValue) * 100), 100)}%` }}
                      />
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" className="w-full mt-4 text-[10px] uppercase font-bold tracking-widest h-8">
                    Contribute
                  </Button>
                </Card>
              ))}
            </div>
          )}

          {(!isAdmin || isStripeConnected) && node && (
            <PaymentForm nodeId={node.id} />
          )}

          {isAdmin && (
            <Card className="bg-white border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Stripe Connection
                </h3>
                {isStripeConnected ? (
                  <>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                      Stripe Account Connected ✅<br/>
                      You are ready to accept dues and sponsorships.
                    </p>
                    <Button variant="secondary" size="sm" onClick={handleViewDashboard} isLoading={isLoadingDashboard}>
                      View Stripe Dashboard
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                      Connect your bank account to start accepting dues and sponsorships.
                    </p>
                    <Button onClick={handleConnectStripe} isLoading={isConnectingStripe} variant="primary" size="sm">
                      Connect with Stripe
                    </Button>
                  </>
                )}
              </div>
            </Card>
          )}
          
          <Card className="bg-navy-900 text-white border-none">
            <h3 className="font-semibold mb-2">Financial Transparency</h3>
            <p className="text-xs text-blue-200 leading-relaxed mb-4">
              {brand?.name} uses a hierarchical split system. Every dollar contributed is automatically distributed between your local node and parent organizations to cover shared infrastructure and services.
            </p>
            <button 
              onClick={() => setIsPolicyModalOpen(true)}
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-blue-300 font-mono hover:text-white transition-colors cursor-pointer group"
            >
              <FileText size={14} className="group-hover:scale-110 transition-transform" />
              <span>View Split Policy</span>
            </button>
          </Card>

          {/* Contributions Feed Preview */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest font-mono">Recent Contributions</h3>
              {can('manage_members') && <Badge variant="gray">{contributions.length}</Badge>}
            </div>
            
            <div className="space-y-3">
              {contributions.slice(0, 3).map((contribution) => (
                <Card key={contribution.id} className="p-3 bg-white/50 border-2 border-black dark:border-white dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    {contribution.imageUrl && (
                      <div className="w-12 h-12 rounded-none overflow-hidden shrink-0">
                        <img src={contribution.imageUrl} alt={contribution.title || 'Contribution image'} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">{contribution.authorName}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{contribution.title}</p>
                    </div>
                    <Badge variant={contribution.status === 'APPROVED' ? 'green' : 'amber'}>
                      {contribution.status}
                    </Badge>
                  </div>
                </Card>
              ))}
              <Button variant="ghost" size="sm" className="w-full text-xs font-bold text-brand-primary">
                View All Community Artifacts
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column: Transaction History */}
        <div className="lg:col-span-2">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {isAdmin ? 'All Transactions' : 'Your History'}
              </h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input 
                    type="text" 
                    value={txSearch}
                    onChange={(e) => setTxSearch(e.target.value)}
                    placeholder="Search..." 
                    className="pl-9 pr-3 py-1.5 text-xs border-2 border-black dark:border-white rounded-none focus:outline-none focus:border-blue-500 w-40"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const order: Array<'ALL' | 'PAID' | 'PENDING' | 'FAILED'> = ['ALL', 'PAID', 'PENDING', 'FAILED'];
                    setTxStatusFilter(order[(order.indexOf(txStatusFilter) + 1) % order.length]);
                  }}
                  title={`Filter status: ${txStatusFilter}`}
                >
                  <Filter size={14} />
                  {txStatusFilter !== 'ALL' && <span className="ml-1 text-[10px] font-mono">{txStatusFilter}</span>}
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Spinner />
                <p className="mt-4 text-xs text-gray-500">Loading transactions...</p>
              </div>
            ) : transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[600px]">
                  <thead>
                    <tr className="border-b border-black dark:border-white">
                      <th className="pb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Date</th>
                      {(isAdmin || dependentUserIds.length > 0) && <th className="pb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Member</th>}
                      {includeSubnodes && <th className="pb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Organization</th>}
                      <th className="pb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Description</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Amount</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Status</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(isAdmin ? transactions : myTransactions)
                      .filter((t) => {
                        if (txStatusFilter !== 'ALL' && t.status !== txStatusFilter) return false;
                        if (!txSearch.trim()) return true;
                        const q = txSearch.toLowerCase();
                        return (
                          t.description?.toLowerCase().includes(q) ||
                          t.userName?.toLowerCase().includes(q) ||
                          t.nodeName?.toLowerCase().includes(q)
                        );
                      })
                      .map((t) => (
                      <tr key={t.id} className="group hover:bg-white transition-colors dark:bg-slate-950">
                        <td className="py-4 text-xs text-gray-500">
                          {safeFormat(t.createdAt, 'MMM d, yyyy')}
                        </td>
                        {(isAdmin || dependentUserIds.length > 0) && (
                          <td className="py-4">
                            <div className="flex items-center gap-2">
                              <div className={cn(
                                "w-6 h-6 rounded-none flex items-center justify-center text-[10px] font-bold animate-in fade-in duration-200",
                                t.isAnonymous 
                                  ? "bg-slate-150 text-slate-500 dark:bg-slate-800 dark:text-slate-450" 
                                  : "bg-blue-100 text-blue-700"
                              )}>
                                {t.isAnonymous ? 'A' : (t.userName ? t.userName[0] : '?')}
                              </div>
                              <span className="text-xs font-medium text-gray-900 dark:text-white">
                                {t.isAnonymous 
                                  ? 'Anonymous Benefactor' 
                                  : (t.userId === user?.id ? 'Me' : t.userName)}
                              </span>
                            </div>
                          </td>
                        )}
                        {includeSubnodes && (
                          <td className="py-4">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-white px-1.5 py-0.5 rounded dark:bg-slate-800">
                              {t.nodeName}
                            </span>
                          </td>
                        )}
                        <td className="py-4">
                          <p className="text-xs font-medium text-gray-900 dark:text-white">{t.description}</p>
                        </td>
                        <td className="py-4">
                          <span className="text-xs font-mono font-bold text-gray-900 dark:text-white">
                            ${t.amount.toFixed(2)}
                          </span>
                        </td>
                        <td className="py-4">
                          <Badge variant={
                            t.status === 'PAID' ? 'green' : 
                            t.status === 'PENDING' ? 'amber' : 
                            t.status === 'FAILED' ? 'red' : 'gray'
                          }>
                            {t.status}
                          </Badge>
                        </td>
                        <td className="py-4 text-right">
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
                            <ArrowUpRight size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-12 h-12 bg-white rounded-none flex items-center justify-center mx-auto mb-4 text-gray-300 dark:bg-slate-950">
                  <DollarSign size={24} />
                </div>
                <p className="text-sm font-medium text-gray-500">No transactions found</p>
              </div>
            )}

            {transactionsData && (
              <div className="px-6 pb-6 mt-4">
                <PaginationControls
                  currentPage={transactionsData.number}
                  totalPages={transactionsData.totalPages}
                  isFirst={transactionsData.first}
                  isLast={transactionsData.last}
                  onPageChange={setPage}
                  totalElements={transactionsData.totalElements}
                />
              </div>
            )}
          </Card>
        </div>
      </div>

      <Modal
        isOpen={isPolicyModalOpen}
        onClose={() => setIsPolicyModalOpen(false)}
        title="Revenue Split Policy"
        footer={
          <Button onClick={() => setIsPolicyModalOpen(false)}>I Understand</Button>
        }
      >
        <div className="space-y-6">
          <div className="space-y-3">
             <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 font-mono">Test Calculation Amount</label>
             <div className="flex items-center gap-2">
               <span className="text-gray-400">$</span>
               <input 
                 type="number" 
                 value={splitAmount} 
                 onChange={(e) => setSplitAmount(Number(e.target.value))}
                 className="w-full bg-white border-2 border-black dark:border-white rounded-none px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20 dark:bg-slate-950"
               />
             </div>
          </div>

          <div className="p-4 bg-blue-50 rounded-none border-2 border-black dark:border-white border-blue-100 flex gap-4">
            <div className="w-10 h-10 rounded-none bg-blue-500 text-white flex items-center justify-center shrink-0">
              <DollarSign size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-navy-900 dark:text-white">Automated Distribution</p>
              <p className="text-xs text-blue-700 leading-relaxed">
                Our split-ledger technology ensures that maintenance, legal, and operational costs are covered at every level of the hierarchy automatically.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Current Split Breakdown</h4>
            
            <div className="space-y-3">
              {isSplitsLoading ? (
                <div className="flex justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : splitResults && splitResults.length > 0 ? (
                <SplitBreakdown splits={splitResults} />
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic text-center p-4 bg-white rounded-none border-2 border-black dark:border-white border-dashed border-black dark:border-white dark:bg-slate-950">
                  Enter an amount to see the distribution.
                </p>
              )}
            </div>
          </div>

          <p className="text-[10px] text-gray-400 leading-relaxed italic border-t border-black dark:border-white pt-4">
            * Note: Split ratios are determined by the top-level parent organization and may vary depending on your specific Tier and contract terms.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={isManualContributionModalOpen}
        onClose={() => setIsManualContributionModalOpen(false)}
        title="Record Manual Payment"
      >
        <form onSubmit={handleManualContribution} className="space-y-4">
          <Input 
            label="Amount"
            type="number"
            step="0.01"
            value={manualAmount}
            onChange={(e) => setManualAmount(e.target.value)}
            required
            placeholder="e.g. 50.00"
          />
          <Input 
            label="Description"
            value={manualDescription}
            onChange={(e) => setManualDescription(e.target.value)}
            required
            placeholder="e.g. Cash payment for annual dues"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" type="button" onClick={() => setIsManualContributionModalOpen(false)}>Cancel</Button>
            <Button variant="primary" type="submit" isLoading={manualContributionMutation.isPending}>Record Payment</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
